import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  cn,
} from "@heroui/react";
import { addToast } from "@heroui/toast";
import { HandIcon } from '@heroicons/react/outline';
import StatusBadge from './StatusBadge';
import Link from 'next/link';

interface BookStatusCardProps {
  isAvailable: boolean;
  canBorrow?: boolean;
  ownerNickname?: string;
  className?: string;
  title?: string;
  availableText?: string;
  unavailableText?: string;
  borrowButtonText?: string;
}

/**
 * BookStatusCard - A reusable component for displaying book availability status
 */
export default function BookStatusCard({
  isAvailable,
  canBorrow = false,
  ownerNickname,
  className = "",
  title = "Status egzemplarza",
  availableText = "Do wypożyczenia",
  unavailableText = "Wypożyczona",
  borrowButtonText = "Wypożycz tę książkę"
}: BookStatusCardProps) {
  // Function to scroll to owner card and show toast
  const handleBorrowClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // Scroll to owner card
    const ownerCard = document.querySelector('[data-owner-card]');
    if (ownerCard) {
      ownerCard.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Add pulsing animation to owner card
      ownerCard.classList.add('pulse-shadow');

      // Find the email button and add pulsing animation
      const emailButton = ownerCard.querySelector('[data-email-button]');
      if (emailButton) {
        emailButton.classList.add('pulse-button');
      }

      // Remove animation classes after 5 seconds
      setTimeout(() => {
        ownerCard.classList.remove('pulse-shadow');
        if (emailButton) {
          emailButton.classList.remove('pulse-button');
        }
      }, 5000);
    }
  };

  return (
    <Card className={`shadow-sm border border-gray-200 ${className}`}>
      <CardHeader className="pb-0 px-6">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      </CardHeader>
      <CardBody className="p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-600">Dostępność:</span>
          <StatusBadge
            status={isAvailable}
            availableText={availableText}
            unavailableText={unavailableText}
          />
        </div>
        {canBorrow && ownerNickname &&
          (
            <Button
              fullWidth
              color="success"
              endContent={<HandIcon className="h-4 w-4" />}
              size="md"
              className="mt-2"
              onClick={handleBorrowClick}
              onPress={

                () => {
                  addToast({
                    title: "Wskazówka",
                    description: "Sprawdź naszego bloga, aby dowiedzieć się więcej o pożyczaniu książek.",
                    classNames: {
                      base: cn([
                        "bg-default-50 dark:bg-background shadow-sm",
                        "border-1",
                        "relative before:content-[''] before:absolute before:z-10",
                        "before:left-0 before:top-[-1px] before:bottom-[-1px] before:w-1",
                        "rounded-l-none border-l-0",
                        "min-w-[350px]",
                        "rounded-md",
                        "flex flex-col items-start",
                        "before:bg-primary border-primary-200 dark:border-primary-100",
                      ]),
                      icon: "w-6 h-6 fill-current",
                    },
                    endContent: (
                      <div className="ms-11 my-2 flex gap-x-2">
                        <Button as={Link} href='/blog' color={"primary"} size="sm" variant="bordered">
                          Sprawdź bloga
                        </Button>
                        <Button as={Link} href='/docs' className="underline-offset-2" color={"primary"} size="sm" variant="light">
                          O Lektuarium
                        </Button>
                      </div>
                    ),
                    color: "primary",
                  });
                }}
            >
              {borrowButtonText}
            </Button>)
        }
      </CardBody>
    </Card>
  );
}